import React, { Component } from 'react';
import styled from 'styled-components';
import { trackAnalytics,
  attachLocale,
  attachCategorySlug,
  attachShopSlug,
  makeExternalUrl,
  resolveAccessibleLabel,
} from '../../../../lib/utils';
import { DOM_SEARCH_BAR } from '../../../../lib/constants';
import ArrowRight from '../../../svgs/ArrowRight';
import ShopNowButton from '../Button/ShopNowButton';

// *** SEARCH RESULTS ***
const SearchResultsDiv = styled.div`
  z-index: ${p => p.theme.elevation[2]};
  position: absolute;
  ${p => p.mobile ? 'position: relative' : ''}
  top: ${p => p.absTop ? p.theme.unit(p.absTop) : p.theme.unit(5.5)};
  width: 100%;
  ${p => p.mobile ? '' : `min-width: ${p.theme.unit(40)};`}
  background-color: ${p => p.theme.c.bg.primary};
  border: solid 1px ${p => p.theme.c.border.primary};
  box-shadow: 0 2px 4px 0 ${p => p.theme.c.border['box-shadow']};
`;

const SearchRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  :hover {
    background-color: ${p => p.theme.c.bg.secondary};
    cursor: pointer;
  }

  ${p => p.theme.typographyRebrand.body.sm}
  color: ${p => p.theme.c.text.primary};
`;

const LogoContainerDiv = styled.div`
  width: ${p => p.theme.unit(8)};
  height: ${p => p.theme.unit(4)};
  text-align: center;
  align-items: center;
  justify-content: center;
  padding: ${p => p.theme.unit(0.5)} ${p => p.theme.unit(1)};
  margin: 0 ${p => p.theme.unit(2)} 0 ${p => p.theme.unit(3)};
  background-color: ${p => p.theme.c.bg.primary}; 
`;

const ShopLogoContainerDiv = styled(LogoContainerDiv)`
  border: solid 1px ${p => p.theme.c.border.primary};
`;

const Logo = styled.img`
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: 100%;
  vertical-align: middle;
`;

const SearchSectionDiv = styled.div`
  padding: ${p => p.theme.unit(2)} 0;
  background-color: ${p => p.theme.c.bg.primary}; 
`;

const BaseSectionNameDiv = styled.div`
  ${p => p.theme.typographyRebrand.heading.sm}
  margin-left: ${p => p.theme.unit(3)};
  color: ${p => p.theme.c.text.primary};

  margin-bottom: ${p => p.theme.unit(1)};
`;

const SearchDividerHr = styled.hr`
  display: block;
  border: 0;
  border-top: 1px solid ${p => p.theme.c.border.primary};
  margin: 0;
  padding: 0;
`;

const SearchHitAnchor = styled.a`
  border-color: ${p => p.theme.c.border.primary};
  flex-grow: 1;
  display: flex;
  align-items: center;
  height: ${p => p.theme.unit(7.5)};
  text-decoration: none;
`;

const HoverAnchorLink = styled(ShopNowButton)`
  margin-right: 40px;

  @media (max-width: ${p => p.theme.breakpoints.m[1]}px) {
    display: none;
  }
`;

const DisplayNameDiv = styled.div`
  ${p => p.theme.typographyRebrand.body.sm}
  color: ${p => p.theme.c.text.primary};
  width: 100%;
`;

// ******** MOBILE *********
const MobileShopLogoContainerDiv = styled(ShopLogoContainerDiv)`
  width: ${p => p.theme.unit(8)};
  height: ${p => p.theme.unit(4)};
`;

const MobileCatLogoContainerDiv = styled(LogoContainerDiv)`
  width: ${p => p.theme.unit(8)};
  height: ${p => p.theme.unit(4)};  
`;

const ShopResult = (props) => {
  const {
    id,
    displayName,
    slug,
    active,
    affiliateUrl,
    recencyOfOffersDays,
    baseMiles,
    amountPerBaseMiles,
    mediumImageUrl,
    mediumImageAltText,
    exclusionsApply,
    multiplier,
    locale,
    tags,
    searchCtaButtonText,
    mobile,
    pageStateModifiers,
    linkTextPrepositions,
    config = {},
  } = props;

  const { secondaryText } = config;

  if (!mediumImageUrl || !displayName) {
    return null;
  }

  const resolvedImageUrl = makeExternalUrl(mediumImageUrl);
  const resolvedShopInternalUrl = attachLocale(attachShopSlug(slug), locale);
  const trackInteral = trackAnalytics(DOM_SEARCH_BAR, `${slug}-search-store`, resolvedShopInternalUrl, tags);

  const shopNowLabel = resolveAccessibleLabel(
    null,
    { displayName },
    linkTextPrepositions,
    'at1',
    searchCtaButtonText,
  );

  const storeInfoLabel = resolveAccessibleLabel(
    null,
    { displayName },
    linkTextPrepositions,
    'for',
    secondaryText,
  );

  const DesktopSearchRow = () => {
    return (
      <SearchRow className="hover-container">
        <div>
          <SearchHitAnchor
            href={trackInteral.url}
            id={trackInteral.dataTrackingId}
            data-track-id={trackInteral.dataTrackingId}
            data-track-click={trackInteral.dataTrack}
            aria-label={storeInfoLabel}>
            <ShopLogoContainerDiv><Logo src={resolvedImageUrl} alt="" /></ShopLogoContainerDiv>
          </SearchHitAnchor>
        </div>
        <DisplayNameDiv>{ displayName }</DisplayNameDiv>
        { searchCtaButtonText && <HoverAnchorLink
          variant='tertiary'
          className="show-hover"
          shopSlug={slug}
          locale={locale}
          trackAnalytics={(redirectUrl) => (
            trackAnalytics(DOM_SEARCH_BAR, `${slug}-search-affiliate`, redirectUrl, tags)
          )}
          accessibleLabel={shopNowLabel}
          buttonText={searchCtaButtonText}
          renderRightIcon={({ className }) => <ArrowRight className={className} />}
        />}
      </SearchRow>
    );
  };

  const MobileSearchRow = () => {
    return (
      <SearchRow>
        <div>
          <SearchHitAnchor
            href={trackInteral.url}
            id={trackInteral.dataTrackingId}
            data-track-id={trackInteral.dataTrackingId}
            data-track-click={trackInteral.dataTrack}
            accessibleLabel={shopNowLabel}>
            <MobileShopLogoContainerDiv><Logo src={resolvedImageUrl} alt="" /></MobileShopLogoContainerDiv>
          </SearchHitAnchor>
        </div>
        <DisplayNameDiv>{ displayName }</DisplayNameDiv>
      </SearchRow>
    );
  };

  return !mobile ? (<DesktopSearchRow />) : (<MobileSearchRow />);
};

const CategoriesResult = (props) => {
  const {
    id,
    name,
    slug,
    categoryIconUrl,
    locale,
    tags,
    mobile,
  } = props;

  if (!categoryIconUrl || !name) {
    return null;
  }

  const resolvedIconUrl = makeExternalUrl(categoryIconUrl);
  const resolvedCatInternalUrl = attachLocale(attachCategorySlug(slug), locale);
  const trackInteral = trackAnalytics(DOM_SEARCH_BAR, `${slug}-search-category`, resolvedCatInternalUrl, tags);

  const DesktopSearchRow = () => {
    return (
      <SearchRow>
        <SearchHitAnchor
          href={trackInteral.url}
          id={trackInteral.dataTrackingId}
          data-track-id={trackInteral.dataTrackingId}
          data-track-click={trackInteral.dataTrack}>
          <LogoContainerDiv><Logo src={resolvedIconUrl} alt="" /></LogoContainerDiv>
          <DisplayNameDiv>{ name }</DisplayNameDiv>
        </SearchHitAnchor>
      </SearchRow>
    );
  };

  const MobileSearchRow = () => {
    return (
      <SearchRow>
        <SearchHitAnchor
          href={trackInteral.url}
          id={trackInteral.dataTrackingId}
          data-track-id={trackInteral.dataTrackingId}
          data-track-click={trackInteral.dataTrack}>
          <MobileCatLogoContainerDiv><Logo src={resolvedIconUrl} alt="" /></MobileCatLogoContainerDiv>
        </SearchHitAnchor>
        <DisplayNameDiv>{ name }</DisplayNameDiv>
      </SearchRow>
    );
  };

  return !mobile ? (<DesktopSearchRow />) : (<MobileSearchRow />);
};

const SearchDropdown = (props) => {
  const {
    shops,
    categories,
    locale,
    absTop,
    mobile = false,
    searchStoreTitle,
    searchCategoriesTitle,
    searchCtaButtonText,
    pageStateModifiers,
  } = props;
  if (!shops || !categories) {
    return null;
  }

  if (!shops.length && !categories.length) {
    return null;
  }

  const shopsComponent = (mobile) => {
    return (
      <SearchSectionDiv>
        <BaseSectionNameDiv>{ searchStoreTitle }</BaseSectionNameDiv>

        {shops.map((s, index) => (
          <ShopResult
            key={`${s.displayName}-${index}`}
            locale={locale}
            searchCtaButtonText={searchCtaButtonText}
            pageStateModifiers={pageStateModifiers}
            {...s}
            mobile={mobile}
          />
        ))}
    </SearchSectionDiv>);
  };

  const categoriesComponent = (mobile) => {
    return (
    <SearchSectionDiv>
      <BaseSectionNameDiv>{ searchCategoriesTitle }</BaseSectionNameDiv>

      {categories.map((c, index) => (
        <CategoriesResult
          key={`${c.name}-${index}`}
          locale={locale}
          {...c}
          mobile={mobile}
        />
      )) }
    </SearchSectionDiv>);
  };

  const DesktopBody = () => {
    const mobile = false;

    return (
      <SearchResultsDiv mobile={mobile} absTop={absTop}>
        {shops.length > 0 && shopsComponent(mobile)}
        {shops.length > 0 && categories.length > 0 && <SearchDividerHr />}
        {categories.length > 0 && categoriesComponent(mobile)}
      </SearchResultsDiv>
    );
  };


  const MobileBody = () => {
    const mobile = true;

    return (
      <SearchResultsDiv mobile={mobile} absTop={absTop}>
        {shops.length > 0 && shopsComponent(mobile)}
        {shops.length > 0 && categories.length > 0 && <SearchDividerHr />}
        {categories.length > 0 && categoriesComponent(mobile)}
      </SearchResultsDiv>
    );
  };

  const renderComp = mobile ? MobileBody() : DesktopBody();
  return renderComp;
};

export default SearchDropdown;
